import { useState } from "react";
import { useAppDispatch } from "../../../../../app/hooks";
import getLocale from "../../../../../common/helpers/getLocale";
import { fetchWorkAgreementTemplateData } from "../../../../talents/talentsSlice";
import { isFulfilled } from "@reduxjs/toolkit";
import { pdf } from "@react-pdf/renderer";
import { downloadBlob } from "../../../../../common/helpers/downloadBlob";
import { CustomButton } from "../../../../../common/components/functional/customButton/CustomButton";
import { CircularProgress } from "@mui/material";
import { LanguageCode } from "../../../../localized/localizedApi";
import { FixedTermPDF } from "./FixedTermPDF";
import { ContinuousPDF } from "./ContinuousPDF";
import { format } from "date-fns";
import { leadingZerosDateFormat } from "../../../../../common/helpers/formatDateTime";
import { WorkAgreementTemplateData } from "../../../../talents/talentAPI";
import { useTranslation } from "react-i18next";

type Props = {
  talentId: string;
  employmentId: string;
  employmentType: string;
};

enum EmploymentType {
  TEMP = "TEMP",
  CONTINUOUS = "INDEFINITE",
}

export const ExportWorkAgreementPDF = ({
  talentId,
  employmentId,
  employmentType,
}: Props) => {
  const dispatch = useAppDispatch();
  const languageCode = getLocale().code;
  const { t } = useTranslation();

  const [isLoading, setIsLoading] = useState(false);

  const constructFileName = (workAgreement: WorkAgreementTemplateData) => {
    const startDate = format(workAgreement.startDate, leadingZerosDateFormat);
    const endDate = workAgreement.endDate
      ? format(workAgreement.endDate, leadingZerosDateFormat)
      : "";
    const timeRange = endDate ? `${startDate}_${endDate}` : `${startDate}`;
    const fileName =
      `${workAgreement.customerOrgName}_${timeRange}_${languageCode}.pdf`.replaceAll(
        " ",
        "_",
      );

    return fileName;
  };

  const downloadPdf = () => {
    if (!talentId || !employmentId) return;
    setIsLoading(true);
    dispatch(
      fetchWorkAgreementTemplateData({
        employmentId,
        talentId,
        language: languageCode as LanguageCode,
      }),
    ).then(async (action) => {
      if (isFulfilled(action)) {
        const workAgreement = action.payload;
        const pdfDocument =
          employmentType === EmploymentType.TEMP
            ? pdf(<FixedTermPDF workAgreement={workAgreement} />)
            : pdf(<ContinuousPDF workAgreement={workAgreement} />);

        if (!pdfDocument) {
          setIsLoading(false);
          return;
        }
        const fileName = constructFileName(workAgreement);
        const blob = await pdfDocument.toBlob();
        downloadBlob(blob, fileName);
        setIsLoading(false);
      }
    });
  };

  return (
    <CustomButton
      onClick={downloadPdf}
      disabled={isLoading}
      variant="outlined"
      sx={{ borderRadius: "24px" }}
    >
      {!isLoading ? (
        t("workAgreementDocument:downloadWorkAgreement")
      ) : (
        <CircularProgress size={16} sx={{ marginTop: "2px" }} />
      )}
    </CustomButton>
  );
};
